import { Head } from "@inertiajs/react"
import { FileQuestion } from "lucide-react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import { dashboardPath, signInPath } from "@/routes"

/**
 * NotFound — role-scoped 404 page (UX Spec §6).
 *
 * CTPO-locked: identical wording for "URL doesn't exist" and "URL exists but
 * not yours" — we never hint at resource existence. Rendered by the Rails
 * `errors#not_found` action with HTTP 404 for both true misses and
 * authorization-failed requests.
 *
 * CTA target depends on auth state:
 *   - authed → `dashboardPath()` (the shell picks the right one per role)
 *   - unauthed → `/sign_in`
 */
export default function NotFound() {
  const { t } = useTranslation()
  const { currentUser } = useCurrentContext()

  const isAuthed = currentUser !== null
  const ctaHref = isAuthed ? dashboardPath() : signInPath()
  const ctaLabel = isAuthed
    ? t("not_found.cta_dashboard")
    : t("not_found.cta_sign_in")

  return (
    <>
      <Head title={t("not_found.title")} />
      <main
        className="bg-background flex min-h-screen items-center justify-center px-4"
        tabIndex={-1}
        data-testid="not-found-page"
      >
        <EmptyState
          icon={FileQuestion}
          title={t("not_found.title")}
          description={t("not_found.description")}
          actions={
            <Button asChild>
              {/*
                Plain <a> on purpose. Server renders a 404 status — the Inertia
                runtime may or may not be initialised depending on how the user
                got here (e.g. direct URL hit from a bookmark). A plain anchor
                navigates reliably in both cases; Inertia picks up on the other
                side if the destination is an Inertia page.
               */}
              <a href={ctaHref}>{ctaLabel}</a>
            </Button>
          }
        />
      </main>
    </>
  )
}
